import { useEffect, useState } from "react";
import { Users, Globe, ShoppingCart, Package, CreditCard, AlertTriangle, IndianRupee, Store } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { fmtINR } from "../../lib/api";
import { StatCard, PageHeader, LoadingRows, TrendUp } from "../../components/Shared";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/revenue").then((r) => setRevenue(r.data.series));
  }, []);

  if (!stats) return <LoadingRows rows={5} />;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Command Center" subtitle="Platform-wide performance, revenue and tenant health." />

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {/* MRR hero */}
        <div data-testid="mrr-card" className="md:col-span-2 lg:col-span-2 rounded-xl p-8 relative overflow-hidden text-white glow" style={{ backgroundImage: "linear-gradient(135deg, var(--p1), var(--p2))" }}>
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10 animate-float" />
          <span className="overline-label !text-white/75">Monthly Recurring Revenue</span>
          <p className="font-display font-extrabold text-5xl tracking-tighter mt-4">{fmtINR(stats.mrr)}</p>
          <p className="text-sm opacity-60 mt-3 flex items-center gap-2">
            <TrendUp>{stats.active_websites} active websites</TrendUp>
            <span>·</span><span>{fmtINR(stats.total_revenue)} collected all-time</span>
          </p>
        </div>
        <StatCard icon={Users} label="Partners" value={stats.partners} sub={`${stats.active_partners} active`} testid="stat-partners" delay={0.05} />
        <StatCard icon={Globe} label="Websites" value={stats.websites} sub={`${stats.active_websites} active`} testid="stat-websites" delay={0.1} />
        <StatCard icon={Store} label="Business Owners" value={stats.owners} testid="stat-owners" delay={0.15} />
        <StatCard icon={ShoppingCart} label="Orders" value={stats.orders} testid="stat-orders" delay={0.2} />
        <StatCard icon={Package} label="Products" value={stats.products} testid="stat-products" delay={0.25} />
        <StatCard icon={AlertTriangle} label="Failed Payments" value={stats.failed_payments} sub={`${stats.pending_invoices} invoices pending`} testid="stat-failed" delay={0.3} />

        {/* Revenue chart */}
        <div data-testid="revenue-chart" className="md:col-span-3 lg:col-span-4 bg-card border border-border rounded-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="overline-label">Subscription Revenue</span>
              <p className="font-display font-bold text-xl tracking-tight mt-1">Last 6 months</p>
            </div>
            <IndianRupee className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue} margin={{ left: 0, right: 8, top: 4 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C9A96E" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C9A96E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => [fmtINR(v), "Revenue"]} />
                <Area type="monotone" dataKey="revenue" stroke="#C9A96E" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
